import { useEffect, useMemo, useRef, useState } from 'react';

import { useSession } from '@ais-ui/websockets';
import { useMutation, useQuery, UseQueryResult } from '@tanstack/react-query';

import { useCredentials } from '@ais-ui/auth';
import { useBasePath } from '@ais-ui/custom-hooks';
import {
  Configuration,
  HistoricalTrack,
  type Port,
  Ship,
  ShipApi,
  ShipHistoricalPost,
} from '@ais-ui/api-spec/portserver';
import { getBasePath } from './getAISBasePath';
import { getAuthHeaderFromCredentials } from '@ais-ui/utils';
import { useApiClientWrapper } from './apiClientWrapper';

export const useShipApi = () => {
  const credentials = useCredentials();
  const { sessionId } = useSession();
  const { protocol, hostname } = useBasePath();

  const apiClientWrapper = useApiClientWrapper();

  let basePath = getBasePath();
  let configuration: Configuration;

  if (hostname.includes('localhost')) {
    const username = credentials?.username || '';
    const password = credentials?.password || '';

    const authHeader = getAuthHeaderFromCredentials(username, password);
    configuration = new Configuration({
      basePath: `${basePath}api/portserver`,
      headers: {
        'X-Application-Name': 'marinepulse',
        Authorization: authHeader,
      },
    });
  } else {
    basePath = `${basePath}aisweb/api/portserver`;
    configuration = new Configuration({
      basePath: basePath,
      headers: {
        'Aisweb-Session': sessionId,
        'X-Application-Name': 'marinepulse',
      },
    });
  }

  const shipApi = useMemo(() => new ShipApi(configuration), [configuration]);

  // The wrapper is used to get error messages from the backend, as they are otherwise thrown out
  return apiClientWrapper(shipApi) as ShipApi;
};

export const usePortServerShipSearch = (
  search: string
): UseQueryResult<Record<'records', Array<Port>> | null> => {
  const shipApi = useShipApi();

  return useQuery(['shipSearchGet', search], async () => {
    return await shipApi.shipSearchQuerystringGet({
      querystring: search,
    });
  });
};

export const useMonitorShipSelections = (enabled = false) => {
  const shipApi = useShipApi();

  return useQuery({
    queryKey: ['selectShipPolling'],
    queryFn: async () => {
      const data = await shipApi.shipSelectGet();
      return data;
    },
    enabled: enabled,
    refetchInterval: 5000, // Poll every 5 seconds
    refetchIntervalInBackground: true, // Continue polling even when tab is not focused
    staleTime: 0, // Consider data stale immediately to ensure fresh checks
    retry: 3,
    retryDelay: 1000,
  });
};

interface ShipData {
  imo: number;
}

interface UseShipTrackFetchProps {
  shipData?: ShipData;
  beginDate: Date;
  endDate: Date;
  resolution: string;
  maxAttempts?: number;
}

export interface UseShipTrackFetchResult {
  data: HistoricalTrack | undefined;
  isLoading: boolean;
  isError: boolean;
  error: Error | null;
  isSuccess: boolean;
  trackId: string | null;
  refetch: () => void;
}

export const useShipTrackFetch = ({
  shipData,
  beginDate,
  endDate,
  resolution,
  maxAttempts = 30,
}: UseShipTrackFetchProps): UseShipTrackFetchResult => {
  const shipApi = useShipApi();
  const [trackId, setTrackId] = useState<string | null>(null);
  const [completedData, setCompletedData] = useState<HistoricalTrack | null>(
    null
  );
  const attemptsRef = useRef(0);
  const initiatedRef = useRef(false);
  const hasCompletedRef = useRef(false);

  const trackMutation = useMutation<string, Error, ShipHistoricalPost>({
    mutationFn: async (
      shipHistoricalData: ShipHistoricalPost
    ): Promise<string> => {
      try {
        const response = await shipApi.shipHistoricalTrackPost({
          json: shipHistoricalData,
        });
        return response.id as string;
      } catch (error) {
        console.error('Error posting ship track request:', error);
        throw error;
      }
    },
    onSuccess: (id) => {
      setTrackId(id);
      attemptsRef.current = 0;
      hasCompletedRef.current = false;
    },
  });

  const trackQuery = useQuery<HistoricalTrack, Error>({
    queryKey: ['shipTrack', trackId],
    queryFn: async () => {
      try {
        if (!trackId) throw new Error('No track ID available');

        const response = await shipApi.shipHistoricalTrackIdGet({
          id: trackId,
        });

        // Check if we have actual data (200 response) or empty result (202 response)
        if (!response || !response.ship || !response.track) {
          // Empty or incomplete response means still processing (202)
          attemptsRef.current += 1;
          if (attemptsRef.current >= maxAttempts) {
            throw new Error('Maximum polling attempts reached');
          }
          throw new Error('Still processing');
        }

        // We have complete data (200 response)
        return response;
      } catch (error) {
        console.error('Error fetching ship track:', error);
        throw error instanceof Error ? error : new Error('Unknown error');
      }
    },
    enabled: !!trackId && !hasCompletedRef.current,
    refetchInterval: (data) => {
      // If we have complete data, stop polling
      if (data && data.ship && data.track) {
        return false;
      }
      // Otherwise, continue polling every second
      return 1000;
    },
    refetchIntervalInBackground: true,
    retry: (failureCount, error) => {
      // Only retry if it's a "Still processing" error and we haven't exceeded max attempts
      if (error instanceof Error && error.message === 'Still processing') {
        return failureCount < maxAttempts;
      }
      // For other errors, use default retry logic (3 attempts)
      return failureCount < 3;
    },
    staleTime: 1000 * 60 * 10, // 10 minutes
    cacheTime: 1000 * 60 * 30, // 30 minutes
  });

  // Clean up trackId when we have complete data
  useEffect(() => {
    if (
      trackQuery.data?.ship &&
      trackQuery.data?.track &&
      trackId &&
      !hasCompletedRef.current
    ) {
      hasCompletedRef.current = true;
      setCompletedData(trackQuery.data);
      // Delay clearing trackId to ensure the data is properly cached
      const timer = setTimeout(() => {
        setTrackId(null);
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [trackQuery.data, trackId]);

  // Reset flags when input parameters change
  useEffect(() => {
    initiatedRef.current = false;
    hasCompletedRef.current = false;
    setCompletedData(null);
  }, [shipData?.imo, beginDate, endDate, resolution]);

  // Store mutation for explicit calling
  const initiateMutation = useMutation({
    mutationKey: [
      'initiateShipTrack',
      shipData,
      beginDate,
      endDate,
      resolution,
    ],
    mutationFn: async () => {
      if (shipData && beginDate && endDate && resolution) {
        await trackMutation.mutateAsync({
          ship: shipData,
          begin: beginDate,
          end: endDate,
          resolution: resolution,
        });
      }
    },
    onError: (error) => {
      console.error('Failed to initiate ship track:', error);
    },
  });

  useEffect(() => {
    if (
      shipData &&
      beginDate &&
      endDate &&
      resolution &&
      !initiatedRef.current &&
      !trackId &&
      !hasCompletedRef.current
    ) {
      // Check if we already have cached data for this query
      const cachedData = trackQuery.data;
      if (cachedData?.ship && cachedData?.track) {
        // We already have complete data in cache, no need to fetch again
        hasCompletedRef.current = true;
        return;
      }

      initiatedRef.current = true;
      initiateMutation.mutate();
    }
  }, [
    shipData,
    beginDate,
    endDate,
    resolution,
    trackId,
    trackQuery.data,
    initiateMutation,
  ]);

  const data = completedData || trackQuery.data;
  const hasCompleteData = !!(data?.ship && data?.track);

  return {
    data,
    isLoading:
      trackMutation.isLoading ||
      (trackQuery.isLoading && !hasCompleteData && !hasCompletedRef.current),
    isError: trackMutation.isError || trackQuery.isError,
    error: (trackMutation.error || trackQuery.error) as Error | null,
    isSuccess: hasCompleteData,
    trackId,
    refetch: trackQuery.refetch,
  };
};

export const useShipPortCallsHistory = (shipData: Ship | undefined) => {
  const shipApi = useShipApi();

  return useQuery(
    ['shipPortCalls', shipData],
    async () => {
      const endDate = new Date();
      const startDate = new Date();
      startDate.setDate(startDate.getDate() - 90);

      const requestBody = {
        json: {
          ship: shipData,
          begin: startDate,
          end: endDate,
        },
      };

      const result = await shipApi.shipHistoricalPortPost(requestBody);

      return result;
    },
    {
      enabled: !!shipData,
      refetchOnWindowFocus: false,
      refetchOnMount: false,
      staleTime: Infinity,
      retry: (failureCount, error: any) => {
        // Check if error has a status code in the 4xx range
        const statusCode =
          error?.response?.parsedBody?.code ||
          error?.response?.status ||
          error?.status;

        if (statusCode && statusCode >= 400 && statusCode < 500) {
          // Don't retry client errors
          return false;
        }

        // For other errors, retry up to 3 times
        return failureCount < 3;
      },
    }
  );
};
